import React, { useEffect, useMemo, useState } from "react";
import { getAdminOperationalSnapshot } from "../../../api/adminDashboard";

const STATUS = {
  Healthy: "healthy",
  Attention: "attention",
  "High risk": "high-risk",
};

const formatUnavailable = (signals = []) => {
  const map = {
    weatherRisk: "Weather",
    paymentsSuccess: "Payments",
    equipmentAvailability: "Availability",
    overdueReturns: "Overdue returns",
    bookingDensity: "Booking density",
  };

  return signals
    .map((key) => map[key] || key)
    .filter(Boolean)
    .join(", ");
};

const clamp01 = (value) => Math.max(0, Math.min(1, value));

const lerp = (a, b, t) => a + (b - a) * t;

const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "").trim();
  const full = normalized.length === 3
    ? normalized.split("").map((c) => c + c).join("")
    : normalized;
  const int = parseInt(full, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
    .join("")}`;

const lerpHex = (aHex, bHex, t) => {
  const a = hexToRgb(aHex);
  const b = hexToRgb(bHex);
  return rgbToHex({
    r: lerp(a.r, b.r, t),
    g: lerp(a.g, b.g, t),
    b: lerp(a.b, b.b, t),
  });
};

// Progressive accent across the full 0..100 range, passing through the threshold colors.
const scoreToAccent = (score) => {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const red = "#b3362d"; // --admin-coral
  const yellow = "#c5a25e"; // --admin-sun
  const green = "#2d6a4f"; // --admin-mint

  if (s <= 50) return lerpHex(red, yellow, clamp01(s / 50));
  if (s <= 80) return lerpHex(yellow, green, clamp01((s - 50) / 30));
  return green;
};

const OrbGauge = ({ score, label, generatedAt, signalsCount }) => {
  const radius = 64;
  const stroke = 14;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setProgress(score));
    return () => window.cancelAnimationFrame(id);
  }, [score]);

  const dashOffset = useMemo(() => {
    const pct = Math.max(0, Math.min(100, progress)) / 100;
    return circumference * (1 - pct);
  }, [circumference, progress]);

  const tone = STATUS[label] || "attention";
  const accent = useMemo(() => scoreToAccent(score), [score]);
  const updated = generatedAt
    ? new Date(generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div
      className={`admin-orb admin-orb--${tone}`.trim()}
      style={{ "--orb-accent": accent }}
    >
      <svg
        className="admin-orb__svg"
        height="100%"
        width="100%"
        viewBox={`0 0 ${radius * 2} ${radius * 2}`}
      >
        <circle
          className="admin-orb__track"
          strokeWidth={stroke}
          fill="transparent"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          className="admin-orb__progress"
          strokeWidth={stroke}
          fill="transparent"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={dashOffset}
        />
      </svg>

      <div className="admin-orb__center">
        <div className="admin-orb__score">{score}</div>
        <div className="admin-orb__label">{label}</div>
        <div className="admin-orb__meta">
          {updated ? <span>{`Updated ${updated}`}</span> : null}
        </div>
      </div>
    </div>
  );
};

const OperationalSnapshotCard = ({ isActive }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await getAdminOperationalSnapshot();
        if (cancelled) return;
        if (typeof payload !== "object" || payload === null) {
          throw new Error("Operational snapshot returned an unexpected response.");
        }
        setData(payload);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load operational snapshot.");
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isActive]);

  const snapshot = useMemo(() => {
    if (!data) return null;
    // Be tolerant to server casing variations.
    return {
      score: data.score ?? data.Score,
      label: data.label ?? data.Label,
      signals: data.signals ?? data.Signals,
      unavailableSignals: data.unavailableSignals ?? data.UnavailableSignals,
      brief: data.brief ?? data.Brief,
      generatedAt: data.generatedAt ?? data.GeneratedAt,
    };
  }, [data]);

  const availableSignalsCount = useMemo(() => {
    if (!snapshot?.signals) return 0;
    return Object.values(snapshot.signals).filter((s) => !s?.unavailable && (s?.weight || 0) > 0).length;
  }, [snapshot]);

  const limitedData = useMemo(
    () => formatUnavailable(snapshot?.unavailableSignals),
    [snapshot]
  );

  return (
    <section className="admin-section admin-ops admin-animate admin-animate--delay-3">
      <header>
        <div>
          <p className="admin-section__eyebrow">Operational snapshot</p>
          <h2>Operational snapshot</h2>
        </div>
        {limitedData ? <p className="admin-ops__limited">{`Limited data: ${limitedData}`}</p> : null}
      </header>

      {loading ? (
        <div className="admin-ops__grid admin-ops__skeleton">
          <div className="admin-ops__left">
            <div className="admin-skel admin-skel--line" />
            <div className="admin-skel admin-skel--line" />
            <div className="admin-skel admin-skel--line admin-skel--short" />
            <div className="admin-skel admin-skel--spacer" />
            <div className="admin-skel admin-skel--line" />
            <div className="admin-skel admin-skel--line admin-skel--short" />
          </div>
          <div className="admin-ops__right">
            <div className="admin-skel admin-skel--orb" />
          </div>
        </div>
      ) : error ? (
        <p className="admin-empty">{error}</p>
      ) : !snapshot ? (
        <p className="admin-empty">No operational snapshot available.</p>
      ) : (
        <div className="admin-ops__grid">
          <div className="admin-ops__left">
            <div className="admin-ops__brief">
              <p className="admin-ops__drivers-title">Daily brief</p>
              {Array.isArray(snapshot?.brief?.sentences)
                ? snapshot.brief.sentences.map((sentence, idx) => (
                    <p key={`brief-${idx}`} className="admin-ops__sentence">
                      {sentence}
                    </p>
                  ))
                : null}
            </div>
            {Array.isArray(snapshot?.brief?.topDrivers) && snapshot.brief.topDrivers.length ? (
              <>
                <p className="admin-ops__drivers-title">Top drivers</p>
                <ul className="admin-ops__drivers">
                  {snapshot.brief.topDrivers.slice(0, 4).map((driver, idx) => (
                    <li key={`driver-${idx}`}>{driver}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>

          <div className="admin-ops__right">
            <OrbGauge
              score={snapshot.score ?? 0}
              label={snapshot.label ?? "Attention"}
              generatedAt={snapshot.generatedAt}
              signalsCount={availableSignalsCount || 1}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default OperationalSnapshotCard;
